import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetSALogs, useGetTenants, useGetSAUsers } from "api/super-admin/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import classNames from "classnames";
import SALogsFilters from "./components/SALogsFilters";
import SALogsCards from "./components/SALogsCards";

const hasChangedData = (changedData) => {
  if (!changedData || changedData === "null") return false;
  if (typeof changedData === "object") {
    return Object.keys(changedData).length > 0;
  }
  if (typeof changedData === "string") {
    return (
      changedData.length > 0 &&
      changedData !== "[]" &&
      changedData !== "{}"
    );
  }
  return false;
};

const Logs = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userIdFilter, setUserIdFilter] = useState(null);
  const [tenantFilter, setTenantFilter] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const { data: tenantsData } = useGetTenants(1, 1000);
  const { data: usersResponse } = useGetSAUsers(1, 1000, "", "", true);

  const tenantOptions = useMemo(
    () =>
      tenantsData?.data?.map((row) => ({
        value: row.id,
        label: row.name,
      })) || [],
    [tenantsData]
  );

  const userOptions = useMemo(() => {
    const usersList = usersResponse?.results || usersResponse || [];
    return (Array.isArray(usersList) ? usersList : []).map((user) => ({
      value: user.id,
      label: user.user_detail?.full_name || user.username,
    }));
  }, [usersResponse]);

  const { data: logsResponse, pagination, isLoading } = useGetSALogs(
    page,
    pageSize,
    userIdFilter?.value,
    tenantFilter?.value
  );

  const serverPageCount = pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const isTheme2 = shellThemeVariant === "2";
  const LogsTable = isTheme2 ? ReactTable2 : ReactTable;

  const toggleExpand = useCallback((id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const renderChangedData = useCallback((changedData) => {
    if (!changedData) return <span>-</span>;

    let data = changedData;
    if (typeof changedData === "string") {
      try {
        data = JSON.parse(changedData);
      } catch (e) {
        return <span>{changedData}</span>;
      }
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      return (
        <div className="pl-2">
          {Object.entries(data).map(([key, value], idx) => (
            <div key={idx} className="mb-1" style={{ fontSize: "13px" }}>
              <strong style={{ textTransform: "capitalize" }}>{key.replace(/_/g, " ")}:</strong>{" "}
              {String(value)}
            </div>
          ))}
        </div>
      );
    }

    const nodes = Array.isArray(data) ? data : [];
    if (!nodes.length) return <span>-</span>;

    return (
      <div className="pl-2">
        {nodes.map((node, idx) => {
          if (typeof node === "object" && node.children) {
            const text = (node?.children || [])
              .map((c) => (c && typeof c.text === "string" ? c.text : ""))
              .join("");
            return (
              <p key={idx} className="mb-1">
                {text || "-"}
              </p>
            );
          }
          if (typeof node === "object") {
            return (
              <div key={idx} className="mb-2 border-bottom pb-2">
                {Object.entries(node).map(([k, v], i) => (
                  <div key={i} className="mb-1" style={{ fontSize: "13px" }}>
                    <strong style={{ textTransform: "capitalize" }}>{k.replace(/_/g, " ")}:</strong>{" "}
                    {String(v)}
                  </div>
                ))}
              </div>
            );
          }
          return (
            <p key={idx} className="mb-1">
              {String(node)}
            </p>
          );
        })}
      </div>
    );
  }, []);

  const rows = useMemo(() => {
    if (!logsResponse) return [];
    const list = logsResponse?.results || logsResponse?.data || logsResponse || [];
    return (Array.isArray(list) ? list : []).map((log, idx) => ({
      id: log?.id || idx + 1,
      user: log?.user?.full_name || "-",
      userName: log?.user?.username || "-",
      userEmail: log?.user?.email || "-",
      userId: log?.user?.id || "-",
      tenant: log?.tenant?.name || "-",
      tenantCode: log?.tenant?.code || "-",
      action: log?.action || "-",
      details: typeof log.details === "object" ? JSON.stringify(log.details) : log.details || "-",
      changedData: log?.changed_data,
      link: log?.link || null,
      createdAt: log?.createdAt
        ? new Date(log.createdAt).toLocaleString()
        : log?.created_at || "-",
    }));
  }, [logsResponse]);

  const logColumns = useMemo(
    () => [
      {
        Header: t("searchInventory.tenant"),
        accessor: "tenant",
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: "600" }}>{row.original.tenant}</div>
            <div style={{ fontSize: "10px", opacity: 0.6 }}>Code: {row.original.tenantCode}</div>
          </div>
        ),
      },
      {
        Header: t("logs.userName"),
        accessor: "user",
        Cell: ({ row }) => (
          <div>
            <div style={{ fontWeight: "600" }}>{row.original.user}</div>
            <div style={{ fontSize: "10px", opacity: 0.6 }}>{row.original.userEmail}</div>
          </div>
        ),
      },
      {
        Header: t("logs.action"),
        accessor: "action",
        Cell: ({ row }) => {
          const { original } = row;
          const clickable = !!original.link;
          return (
            <span
              className={clickable ? "log-action-link" : undefined}
              style={{
                cursor: clickable ? "pointer" : "default",
                textDecoration: clickable ? "underline" : "none",
                color: clickable ? activeColorStyle.backgroundColor : "inherit",
                fontWeight: clickable ? "500" : "normal",
              }}
              onClick={(e) => {
                if (original.link) {
                  e.stopPropagation();
                  navigate(original.link);
                }
              }}
            >
              {original.action}
            </span>
          );
        },
      },
      {
        Header: t("logs.date"),
        accessor: "createdAt",
      },
      {
        Header: "",
        accessor: "id",
        disableSortBy: true,
        width: 40,
        Cell: ({ row }) => {
          const { original } = row;
          const isOpen = !!expandedRows[original.id];
          if (!hasChangedData(original.changedData)) return null;

          return (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(original.id);
              }}
              style={{
                cursor: "pointer",
                display: "inline-block",
                transform: isOpen ? "rotate(270deg)" : "rotate(90deg)",
                transition: "transform 0.15s ease",
              }}
            >
              ▶
            </span>
          );
        },
      },
    ],
    [t, navigate, activeColorStyle, expandedRows, toggleExpand]
  );

  const filters = (
    <SALogsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      userIdFilter={userIdFilter}
      onUserIdFilterChange={(option) => {
        setUserIdFilter(option);
        setPage(1);
      }}
      tenantFilter={tenantFilter}
      onTenantFilterChange={(option) => {
        setTenantFilter(option);
        setPage(1);
      }}
      userOptions={userOptions}
      tenantOptions={tenantOptions}
    />
  );

  const tableHeader =
    !isLoading && pagination?.total > 0 ? (
      <div className="sa-logs-records-header">
        {pagination.total} {t("searchInventory.recordsInTotal")}
      </div>
    ) : null;

  const tableProps = {
    activeColor,
    data: rows,
    columns: logColumns,
    manualPagination: true,
    pageCount: pagination?.pageCount ?? 1,
    pageIndex: page - 1,
    totalItems: pagination?.total,
    pageSize,
    noDataText: t("logs.noRecordsFound"),
    loading: isLoading,
    loadingText: t("logs.loading"),
    onPageChange: (newPageIndex) => setPage(newPageIndex + 1),
    onPageSizeChange: (newSize) => {
      setPageSize(newSize);
      setPage(1);
    },
    renderExpandedRow: (original) => (
      <div className="p-2">{renderChangedData(original.changedData)}</div>
    ),
    isRowExpanded: (original) => !!expandedRows[original.id],
  };

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
    >
      {isTheme2 ? (
        <LogsTable {...tableProps} className="-striped -highlight sticky-stock-table" />
      ) : (
        <LogsTable
          {...tableProps}
          showPaginationTop
          showPaginationBottom={false}
          className="-striped -highlight"
        />
      )}
    </div>
  );

  return (
    <div
      className={classNames("content", "search-sa-logs", {
        "search-sa-logs--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-logs-inset": isTheme2,
      })}
    >
      <SALogsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        tableHeader={tableHeader}
        title={`Super Admin - ${t("routes.saLogs")}`}
      />
    </div>
  );
};

export default Logs;
